<template>
  <div class="settings-block">
    <div class="settings-block__title">
      <div class="settings-block__title-text">{{ t('Tracking domains') }}</div>
      <div class="settings-block__title-buttons">
        <ui-button
          button-style="accent"
          class="v-btn--tooltip mr-0"
          icon="fas fa-plus"
          planLimits="domainsAmount"
          :text="t('Add custom domain')"
          @click="addCustomDomain"
        />
      </div>
    </div>
    <div>
      <div
        v-html="
          t(
            `A tracking domain is required to generate the tracking links for your campaigns: postback URL, tracking pixels, click URL, impression URL, campaign URL.<br>Our system domains (bemobtrk.com, bemobtrcks.com, bemobtracks.com) should be used for testing purposes only. For actual campaign it is required to <b><a target='_blank' href='{0}'>add a custom domain</a></b>.<br><b>Important note:</b> if there is no traffic in campaigns with custom domain for more than 30 days, this domain will be automatically deleted. You can add this domain again if required.<br>Your main domain will be added instead of the deleted domain.<br>Always check the <b>Campaign Links</b> section before renewing the campaign's activity after 30+ days pause.`,
            [`https://docs.bemob.com/${locale}/custom-domain-settings`],
          )
        "
      />
    </div>

    <div class="tracking-domains-list">
      <ui-table
        domains-table
        :sortBy="[
          {
            key: 'domain',
            order: 'asc',
          },
        ]"
        :headers="headers"
        :items="store.trackingDomains.data"
        :loading="!store.trackingDomains.isUploaded"
        :rowsPerPageCount="10"
        :useSearch="false"
        :virtualize="false"
      >
        <template #item.domain="{ item }">
          <div class="d-flex align-center flex-wrap">
            {{ item.domain }}
            <ui-chip
              class="main-chip ml-4"
              chipStyle="round"
              :contentColor="$colors.white"
              v-if="item.id === store.user?.trackingDomainId"
            >
              {{ t('Main') }}
            </ui-chip>
            <ui-chip
              :contentColor="$colors.white"
              chipStyle="round"
              class="https-chip ml-4"
              v-if="item.httpsAvailable"
            >
              <ui-icon class="mr-4" size="8" :color="$colors.white" icon="fas fa-lock" />
              HTTPS
            </ui-chip>
            <ui-chip
              :contentColor="$colors.white"
              chipStyle="round"
              class="ssl-pending ml-4"
              v-if="!item.httpsAvailable && !isNextDnsRecordValidationAfterYear(item)"
            >
              SSL Pending
            </ui-chip>
            <ui-chip
              :contentColor="$colors.white"
              chipStyle="round"
              :backgroundColor="$colors.green"
              class="ml-4"
              v-if="isNextDnsRecordValidationAfterYear(item)"
            >
              {{ t('Client-managed domain') }}
            </ui-chip>
            <ui-chip
              chipStyle="round"
              :backgroundColor="$colors.red"
              :contentColor="$colors.white"
              class="validDnsRecord-chip ml-4"
              v-if="item.validDnsRecord !== 1"
            >
              <ui-icon
                class="mr-4"
                size="8"
                :color="$colors.white"
                icon="fa fa-exclamation-triangle"
              />
              Invalid DNS record
            </ui-chip>
            <ui-tooltip-icon
              v-if="item.validDnsRecord !== 1 && item.nextDnsRecordValidation"
              preset="warning"
              class="ml-4"
              :text="
                t('The next attempt to add a domain automatically will be on {0}', [
                  validDnsDate(item),
                ])
              "
            />
          </div>
        </template>
        <template #item.workspaces="{ item }">
          <span
            v-if="item.workspaceIds && item.workspaceIds.length === 0"
            class="not-assigned"
          >
            {{ t('Not Assigned') }}
          </span>
          <div
            v-if="item.workspaceIds && item.workspaceIds.length > 0"
            class="onePlus-wrp"
          >
            <div class="onePlus-text">
              {{ store.allWorkspacesObject[item.workspaceIds[0]]?.name }}
            </div>
            <v-tooltip
              v-if="item.workspaceIds.length > 1"
              :color="$colors.white"
              content-class="onePlus-tooltip"
              class="ml-8"
              location="top"
            >
              <template #activator="{ props }">
                <div class="onePlus-text onePlus-text--count ml-8" v-bind="props">
                  {{ `+${item.workspaceIds.length - 1}` }}
                </div>
              </template>
              <div
                v-for="workspace in item.workspaceIds.slice(1)"
                :key="workspace"
                class="onePlus-text"
              >
                {{ store.allWorkspacesObject[workspace]?.name }}
              </div>
            </v-tooltip>
          </div>
        </template>
        <template #item.setCampaign="{ item }">
          <ui-select
            class="w100"
            :hideDetails="1"
            v-if="item.custom"
            item-text="nameFull"
            item-title="nameFull"
            :key="'select-' + item.id"
            item-value="id"
            :items="campaignsListWithDefault"
            :modelValue="{
              id: item.campaignId || '',
              nameFull: item.campaignNameFull || t('Account default'),
            }"
            :loading="!store.cache.campaigns.isUploaded"
            :fetchOptions="fetchCampaignOptions"
            @update:modelValue="value => onCampaignChange({ item, value })"
          />
        </template>
        <template #item.setMain="{ item }">
          <ui-button
            button-style="simple"
            class="set-as-main justify-end"
            :class="{
              ['set-as-main__active']: item.id !== store.user?.trackingDomainId,
            }"
            :contentColor="
              item.id !== store.user?.trackingDomainId
                ? $colors.gray20
                : $colors.graphite
            "
            :text="t('Set as main')"
            @click="item.id !== store.user?.trackingDomainId && setAsDefault(item.id)"
            :icon="item.id === store.user?.trackingDomainId && 'fas fa-check'"
          />
        </template>
        <template #item.action="{ item }">
          <ui-button
            v-if="item.custom && item.id !== store.user?.trackingDomainId"
            button-style="simple"
            class="action-button"
            :text="t('Delete')"
            @click="checkDelete(item.id)"
            :contentColor="$colors.red"
            icon="fas fa-trash-alt"
          />
        </template>
      </ui-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import i18n from '@/i18n';
import { useStore } from '@/store';
import { useColors, useGetUsingType, useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';
import {
  deleteTrackingDomainApiRequest,
  putTrackingDomainApiRequest,
  putUserDomainDataApiRequest,
} from '@/services/api/domains-api';
import { getCampaignsApiRequest } from '@/services/api/campaign-api';
import { populateCampaignsStoreCacheApiRequest } from '@/services/api/api-services/campaigns';
import { StatusEnum } from '@/services/models/Enums';
import moment from 'moment';

const props = withDefaults(
  defineProps<{
    format?: string;
  }>(),
  {
    format: 'DD MMM YYYY HH:mm',
  },
);

const { t } = useI18n();
const $colors = useColors();
const store = useStore();
const { getUsingType } = useGetUsingType();

const locale = computed(() => i18n.global.locale.value);

const campaignsListWithDefault = computed(() => [
  {
    nameFull: t('Account default'),
    id: '',
  },
  ...store.cache.campaigns.data,
]);

const headers = computed(() => [
  {
    title: t('Domain'),
    align: 'left',
    value: 'domain',
    sortable: false,
  },
  {
    title: t('Workspaces'),
    value: 'workspaces',
    sortable: false,
  },
  {
    title: t("Domain's root"),
    value: 'setCampaign',
    sortable: false,
  },
  {
    title: '',
    value: 'setMain',
    align: 'right',
    sortable: false,
  },
  {
    title: '',
    value: 'action',
    sortable: false,
  },
]);

const fetchCampaignOptions = computed(() => {
  if (store.cache.campaigns.isAllInCache) {
    return undefined;
  }
  return ({ search }: { search: string }) =>
    getCampaignsApiRequest({
      status: StatusEnum.active,
      search,
      limit: 200,
      orderBy: 'updated',
      orderDirection: 'desc',
    });
});

const validDnsDate = (item: { nextDnsRecordValidation?: string }) =>
  moment(item.nextDnsRecordValidation).format(props.format);

const isNextDnsRecordValidationAfterYear = (item: { nextDnsRecordValidation?: string }) =>
  moment().add(1, 'year').isBefore(item.nextDnsRecordValidation);

const checkDelete = (id: string) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('All selected items will be deleted permanently if you press OK.'),
      onConfirm: () => {
        store.setLoading(true);
        deleteTrackingDomainApiRequest(id)
          .then(() => {
            $emitter.emit('toast-success', t('Domain deleted'));
          })
          .catch(
            (errorResponse: {
              response?: { data?: { errors?: { id?: unknown[] } } };
            }) => {
              const idErrors = errorResponse.response?.data?.errors?.id?.[0];
              if (!Array.isArray(idErrors)) return;
              for (const item of idErrors) {
                const usingType = getUsingType({ type: item.usingType });
                const message = t('{0} is used by {1} {2}', [
                  item.usedName,
                  usingType,
                  item.usingName,
                ]);
                $emitter.emit('toast-warning', message, {
                  dontHide: true,
                });
              }
            },
          )
          .finally(() => {
            store.setLoading(false);
          });
      },
    },
  });
};

const setAsDefault = (id: string) => {
  store.setLoading(true);
  return putUserDomainDataApiRequest({ trackingDomainId: id })
    .then((res: unknown) => {
      store.mergeUser(res as unknown as Record<string, unknown>);
    })
    .finally(() => (store.setLoading(false)));
};

const addCustomDomain = () => {
  $emitter.emit('open-dialog', {
    name: 'AddCustomTrackingDomainDialog',
  });
};

const onCampaignChange = ({
  item,
  value,
}: {
  item: { id: string; campaignId?: string };
  value?: string;
}) => {
  if (typeof value === 'undefined' || item.campaignId === value) return;
  store.setLoading(true);
  store.trackingDomains.isUploaded = false;
  putTrackingDomainApiRequest(item.id, {
    campaignId: value,
  }).finally(() => {
    store.setLoading(false);
  });
};

onMounted(async () => {
  if (!store.cache.campaigns.isUploaded) {
    await populateCampaignsStoreCacheApiRequest();
  }
});
</script>

<style lang="scss" scoped>
@import '@/styles/pages/settings/domains';
</style>

<style lang="scss">
.tracking-domains-list {
  .ui-table__inner {
    min-width: 800px;
  }

  .table-header,
  .table-row {
    flex-grow: 1;

    .table-cell {
      flex: initial;
    }

    .table-cell:nth-child(1) {
      word-break: break-word;
      flex-grow: 1;
      flex-shrink: 1;
      min-width: 100px;
    }

    .table-cell:nth-child(2) {
      width: 150px !important;
      flex-shrink: 0;
    }

    .table-cell:nth-child(3) {
      width: 300px !important;
      flex-shrink: 0;
    }

    .table-cell:nth-child(4) {
      width: 100px !important;
      justify-content: flex-end;
      flex-shrink: 0;
    }

    .table-cell:nth-child(4):lang(es),
    .table-cell:nth-child(4):lang(ru) {
      width: 150px !important;
    }

    .table-cell:nth-child(5) {
      width: 55px !important;
      flex-shrink: 0;
    }

    .table-cell:nth-child(5):lang(es),
    .table-cell:nth-child(5):lang(ru) {
      width: 70px !important;
    }
  }
}
</style>
